/* platform/platformStore — tenancy data store (Phase 7A: backend-backed).
   ─────────────────────────────────────────────────────────────────────
   Loads from backend on mount. SA callers get full platform data; OA callers
   get org-scoped data. Falls back to the static seed when the backend is
   unreachable or the caller lacks platform permissions. */
import { useState, useEffect } from 'react';

/* ── Exported helpers (used by views for tone/percentage) ─── */
export const PLAN_TONES = {
  Enterprise:    'purple', ENTERPRISE:    'purple',
  Business:      'blue',   BUSINESS:      'blue',
  Professional:  'blue',   PROFESSIONAL:  'blue',
  Growth:        'blue',   GROWTH:        'blue',
  Starter:       'gray',   STARTER:       'gray',
  Trial:         'amber',  TRIAL:         'amber',
  Custom:        'amber',  CUSTOM:        'amber',
};

export const statusTone = (s) => ({
  ACTIVE: 'green', LIVE: 'green', ENABLED: 'green', PAID: 'green', RESOLVED: 'green', CERTIFIED: 'green',
  DRAFT: 'slate', ORG_ADMIN_PENDING: 'amber',
  TRIAL: 'blue', ONBOARDING: 'blue', CONFIGURING: 'amber', PENDING: 'amber',
  SUSPENDED: 'red', OVERDUE: 'red', DOWN: 'red', REVOKED: 'red', INACTIVE: 'gray', ARCHIVED: 'gray',
}[s] || 'gray');

export const pct = (n, t) => (t ? Math.min(100, Math.round((n / t) * 100)) : 0);

/* ── Static seed — used as offline/permission fallback ────── */
const seed = () => ({
  organizations: [],
  plans: [
    { id: 'starter',    name: 'Starter',    price: 42000,  seats: 25,   tone: 'gray'   },
    { id: 'growth',     name: 'Growth',     price: 185000, seats: 100,  tone: 'blue'   },
    { id: 'enterprise', name: 'Enterprise', price: 480000, seats: 0,    tone: 'purple' },
    { id: 'custom',     name: 'Custom',     price: null,   seats: null, tone: 'amber'  },
  ],
  licenses:    [],
  partners:    [],
  audit:       [],
  branding:    {},
  workspaces:  [],
  members:     [],
  memberships: [],
  loading:     false,
  error:       null,
});

/* ── Normalisers: backend → store shape ──────────────────── */
const normOrg = (o) => {
  const sub = Array.isArray(o.subscriptions) ? o.subscriptions[0] : null;
  return {
    id:            o.id,
    name:          o.name,
    industry:      o.industry    || '',
    country:       o.country     || '',
    timezone:      o.timezone    || '',
    owner:         o.owner?.name || o.ownerEmail || '',
    ownerEmail:    o.ownerEmail  || '',
    ownerId:       o.ownerId     || null,
    plan:          sub?.plan?.name || o.plan || null,
    planId:        sub?.planId   || null,
    planTier:      sub?.plan?.tier || null,
    planDisplayName:         sub?.plan?.displayName         || null,
    planDescription:         sub?.plan?.description         || null,
    planStatus:              sub?.plan?.status              || null,
    planVersion:             sub?.plan?.version             ?? null,
    planBrandingAllowed:     sub?.plan?.brandingAllowed     ?? false,
    planCustomDomainAllowed: sub?.plan?.customDomainAllowed ?? false,
    planAiEnabled:           sub?.plan?.aiEnabled           ?? false,
    planAnalyticsEnabled:    sub?.plan?.analyticsEnabled    ?? false,
    planMaxStorageGb:        sub?.plan?.maxStorageGb        ?? null,
    planMaxAgents:           sub?.plan?.maxAgents           ?? null,
    planMaxBots:             sub?.plan?.maxBots             ?? null,
    planMaxApiCallsPerDay:   sub?.plan?.maxApiCallsPerDay   ?? null,
    planMaxMessagesPerMonth: sub?.plan?.maxMessagesPerMonth ?? null,
    planMaxWorkspaces:       sub?.plan?.maxWorkspaces       ?? null,
    planFeatureKeys: Array.isArray(sub?.plan?.featureKeys) ? sub.plan.featureKeys : [],
    featureOverrides: o.featureOverrides ?? null,
    subscriptionId:     sub?.id           || null,
    subscriptionStatus: sub?.status       || null,
    billingCycle:       sub?.billingCycle || null,
    billingRef:         sub?.billingRef   || null,
    contractRef:        sub?.contractRef  || null,
    autoRenew:          sub?.autoRenew    ?? null,
    trialEndsAt:        sub?.trialEndsAt  || null,
    subExpiresAt:       sub?.expiresAt    || null,
    subCreatedAt:       sub?.createdAt    || null,
    currency:           sub?.plan?.currency || null,
    startsAt:           sub?.startsAt     || null,
    renewalAt:          sub?.renewalAt    || null,
    status:        o.status     || 'ACTIVE',
    users:         o.userCount   ?? 0,
    region:        o.region      || '',
    created:       o.createdAt   ? String(o.createdAt).split('T')[0] : '',
    partnerId:     o.partnerId   || null,
    maxWorkspaces: o.maxWorkspaces ?? null,
    maxUsers:      o.maxUsers      ?? null,
    maxAgents:     o.maxAgents     ?? null,
    maxBots:       o.maxBots       ?? null,
  };
};

const normWorkspace = (w) => ({
  id:      w.id,
  org:     w.orgId,
  name:    w.name,
  members: w._count?.memberships ?? w.memberCount ?? 0,
  modules: w.modules     || [],
  status:  (w.status     || 'ACTIVE').toUpperCase(),
  created: w.createdAt   ? String(w.createdAt).split('T')[0] : '',
});

const AVATAR_TONES = ['#125AB0', '#0E8C5A', '#A8508A', '#C26A1F', '#6E3FBA', '#B5740E'];
const toneFor = (str) => AVATAR_TONES[(str || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_TONES.length];

const normMember = (m) => ({
  id:         m.id,
  org:        m.orgId,
  name:       m.name,
  email:      m.email,
  orgRole:    m.orgRole || 'MEMBER',
  tone:       toneFor(m.name),
  status:     (m.status || 'ACTIVE').toUpperCase(),
  userId:     m.userId    || null,
  invitedAt:  m.invitedAt || null,
  joinedAt:   m.joinedAt  || null,
  created:    m.joinedAt  ? String(m.joinedAt).split('T')[0] : '',
});

const normPlan = (p) => ({
  id:           p.id,
  code:         p.code,
  name:         p.name,
  displayName:  p.displayName  || p.name,
  description:  p.description  || '',
  tier:         p.tier,
  status:       p.status       || 'ACTIVE',
  version:      p.version      ?? 1,
  isDefault:    p.isDefault    ?? false,
  priceMonthly: p.priceMonthly ?? null,
  priceAnnual:  p.priceAnnual  ?? null,
  currency:     p.currency     || 'INR',
  maxWorkspaces: p.maxWorkspaces ?? null,
  maxStorageGb:  p.maxStorageGb  ?? null,
  maxAgents:     p.maxAgents     ?? null,
  maxBots:       p.maxBots       ?? null,
  maxApiCallsPerDay:   p.maxApiCallsPerDay   ?? null,
  maxMessagesPerMonth: p.maxMessagesPerMonth ?? null,
  featureKeys:  Array.isArray(p.featureKeys) ? p.featureKeys : [],
  tone:         PLAN_TONES[p.tier] || PLAN_TONES[p.name] || 'gray',
});

const normLicense = (l) => ({
  id:        l.id,
  org:       l.orgId,
  type:      l.type    || 'USER',
  status:    l.status  || 'ACTIVE',
  purchased: l.allotted,
  used:      l.used,
  available: Math.max(0, (l.allotted ?? 0) - (l.used ?? 0)),
  renews:    l.renews ? String(l.renews).split('T')[0] : '',
  reason:    l.reason || null,
});

const normPartner = (p) => ({
  id:      p.id,
  name:    p.name,
  type:    ({ IMPLEMENTATION: 'Implementation', RESELLER: 'Reseller', TECHNOLOGY: 'Technology', STRATEGIC: 'Strategic' })[p.type] || p.type,
  tier:    ({ PLATINUM: 'Platinum', GOLD: 'Gold', SILVER: 'Silver', BRONZE: 'Bronze' })[p.tier] || p.tier,
  contact: p.contact      || p.contactName  || '',
  email:   p.email        || p.contactEmail || '',
  region:  p.region       || '',
  orgs:    p.orgCount     ?? 0,
  status:  p.status      || 'ACTIVE',
  since:   p.createdAt    ? String(p.createdAt).split('T')[0] : '',
});

const normAudit = (a) => ({
  id:      a.id,
  who:     a.who || 'System',
  action:  a.action,
  target:  a.target    || '',
  cat:     a.cat,
  sev:     a.sev,
  time:    a.createdAt ? new Date(a.createdAt).toLocaleDateString('en-IN') : '',
  rawDate: a.createdAt ? String(a.createdAt) : null,
});

/* ── Fetch helpers (use apiClient from bridge; falls back to direct fetch) ─ */
const apiFetch = async (path) => {
  const headers = { 'Content-Type': 'application/json' };
  const token = window.__nx_api?.client?.getToken?.();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`/api/v1${path}`, { headers });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

const listItems = (r) => (Array.isArray(r) ? r : (r?.data ?? []));

/* ── Module-level singleton state — shared across all usePlatformData() callers ─
   Root cause of CRM-CONTROL-CENTER-BUG-002 Defect 2 was that each caller of
   usePlatformData() created an independent hook instance, so every component
   mount (including CRM module navigation and cross-tier navigation) fired its
   own loadAll(), producing N org-branding requests per mount (N = org count).
   Moving state and loadAll to module scope ensures exactly one loadAll per
   session-changed event, regardless of how many components call the hook.      */
let _state = seed();
const _subs = new Set();
let _loadPromise = null;
let _hasData = false;
let _sessionListenerAdded = false;

const _notify = () => _subs.forEach(fn => fn());

const _applyUpdate = (updater) => {
  _state = typeof updater === 'function' ? updater(_state) : { ..._state, ...updater };
  _notify();
};

const _sharedLoadAll = async () => {
  if (_loadPromise) return _loadPromise;
  _loadPromise = (async () => {
    _applyUpdate(s => ({ ...s, loading: true, error: null }));
    try {
      const isSA = globalThis.__nx_auth?.user?.platformRole === 'SUPER_ADMIN';

      const [orgsRaw, licensesRaw, partnersRaw, auditRaw, plansRaw] = await Promise.allSettled([
        apiFetch('/orgs?pageSize=100'),
        isSA ? apiFetch('/platform/licenses?pageSize=500') : Promise.resolve(null),
        isSA ? apiFetch('/platform/partners?pageSize=500') : Promise.resolve(null),
        isSA ? apiFetch('/platform/audit?limit=200')       : Promise.resolve(null),
        isSA ? apiFetch('/platform/plans?pageSize=100')    : Promise.resolve(null),
      ]);

      const orgs      = orgsRaw.status      === 'fulfilled' ? listItems(orgsRaw.value).map(normOrg)        : [];
      const licenses  = licensesRaw.status  === 'fulfilled' ? listItems(licensesRaw.value).map(normLicense) : [];
      const partners  = partnersRaw.status  === 'fulfilled' ? listItems(partnersRaw.value).map(normPartner) : [];
      const audit     = auditRaw.status     === 'fulfilled' ? listItems(auditRaw.value).map(normAudit)      : [];
      const plans     = plansRaw.status     === 'fulfilled' ? listItems(plansRaw.value).map(normPlan)       : [];

      /* Load workspaces + members for each org */
      const wsResults = await Promise.allSettled(
        orgs.map(o => apiFetch(`/orgs/${o.id}/workspaces`)),
      );
      const workspaces = wsResults.flatMap((r, i) =>
        r.status === 'fulfilled' ? listItems(r.value).map(w => normWorkspace({ ...w, orgId: orgs[i].id })) : [],
      );

      /* Workspace member fallback: non-SA users lack ORG_VIEW so the orgs fetch
         returns empty, leaving workspaces empty too. Load from the session API
         which returns only the workspaces the caller has active memberships for. */
      let finalWorkspaces = workspaces;
      if (!isSA && workspaces.length === 0) {
        try {
          const sessionWsRaw = await apiFetch('/session/workspaces');
          const sessionWsList = sessionWsRaw?.workspaces ?? [];
          if (sessionWsList.length > 0) finalWorkspaces = sessionWsList.map(normWorkspace);
        } catch (e) {
          console.debug('[platformStore] session/workspaces fallback failed', e?.message);
        }
      }

      const memberResults = await Promise.allSettled(
        orgs.map(o => apiFetch(`/orgs/${o.id}/members?pageSize=200`)),
      );
      const members = memberResults.flatMap((r, i) =>
        r.status === 'fulfilled' ? listItems(r.value).map(m => normMember({ ...m, orgId: orgs[i].id })) : [],
      );

      /* M9: Build memberships join array from workspace member lists */
      const wsMemberResults = await Promise.allSettled(
        workspaces.map(ws => apiFetch(`/workspaces/${ws.id}/members?pageSize=100`)),
      );
      const memberships = wsMemberResults.flatMap((r, i) => {
        if (r.status !== 'fulfilled') return [];
        return listItems(r.value).map(wm => ({
          id:             wm.id || (workspaces[i].id + '_' + (wm.memberId || wm.userId || wm.id)),
          workspace:      workspaces[i].id,
          workspaceId:    workspaces[i].id,
          workspaceName:  workspaces[i].name,
          memberId:       wm.memberId || wm.userId || wm.id,
          wsRole:         wm.wsRole || 'WORKSPACE_USER',
          status:         (wm.status || 'ACTIVE').toUpperCase(),
        }));
      });

      /* M15: Load org branding for each org */
      const brandingResults = await Promise.allSettled(
        orgs.map(o => apiFetch(`/orgs/${o.id}/branding`)),
      );
      const branding = {};
      brandingResults.forEach((r, i) => {
        if (r.status !== 'fulfilled') return;
        const b = r.value;
        if (!b) return;
        branding[orgs[i].id] = {
          primaryColor:  b.primaryColor  || '#125AB0',
          accentColor:   b.accentColor   || '#4692EC',
          secondaryColor: b.secondaryColor || '',
          logoUrl:       b.logoUrl       || null,
          logoText:      b.logoText      || orgs[i].name,
          favicon:       b.favicon       || orgs[i].name[0],
          loginHeadline: b.loginHeadline || ('Welcome to ' + orgs[i].name),
          footer:        b.footer        || '© 2026',
          domain:        b.domain        || '',
          domainStatus:  b.domainStatus  || 'UNVERIFIED',
        };
      });

      _applyUpdate(s => ({
        ...s,
        organizations: orgs.length           ? orgs            : s.organizations,
        plans:         plans.length          ? plans           : s.plans,
        licenses:      licenses.length       ? licenses        : s.licenses,
        partners:      partners.length       ? partners        : s.partners,
        audit:         audit.length          ? audit           : s.audit,
        workspaces:    finalWorkspaces.length ? finalWorkspaces : s.workspaces,
        members:       members.length        ? members         : s.members,
        memberships,
        branding,
        loading: false,
      }));
      _hasData = true;
    } catch (err) {
      _applyUpdate(s => ({ ...s, loading: false, error: err.message }));
    }
  })().finally(() => { _loadPromise = null; });
  return _loadPromise;
};

/* One module-level session listener — never duplicated across hook instances */
const _installSessionListener = () => {
  if (_sessionListenerAdded || typeof window === 'undefined') return;
  _sessionListenerAdded = true;
  window.addEventListener('nx-session-changed', (e) => {
    if (e.detail?.user) {
      _hasData = false;
      // If an unauthenticated load is in progress, _sharedLoadAll() would dedup
      // against it and the retry would be lost. Wait for the in-progress load to
      // complete first, then retry with the now-available auth token.
      const pending = _loadPromise;
      if (pending) {
        void pending.finally(() => { _hasData = false; void _sharedLoadAll(); });
      } else {
        void _sharedLoadAll();
      }
    } else {
      _state = seed();
      _hasData = false;
      _notify();
    }
  });
};

const _sharedUpdate = (patch) =>
  _applyUpdate(s => (typeof patch === 'function' ? patch(s) : { ...s, ...patch }));

const _sharedAddAudit = (entry) =>
  _applyUpdate(s => ({
    ...s,
    audit: [{ id: 'ca_' + Date.now(), who: 'You', sev: 'info', time: 'just now', rawDate: new Date().toISOString(), ...entry }, ...s.audit].slice(0, 200),
  }));

/* ── Hook ───────────────────────────────────────────────────── */
export const usePlatformData = () => {
  const [, rerender] = useState(0);

  useEffect(() => {
    /* Subscribe this component to shared state changes */
    const sub = () => rerender(n => (n + 1) % 1_000_000);
    _subs.add(sub);

    /* Install the singleton session listener (idempotent) */
    _installSessionListener();

    /* Trigger initial load only if the shared store is empty and idle.
       Deduplication via _loadPromise means concurrent mounts don't multiply requests. */
    const token = window.__nx_api?.client?.getToken?.();
    if (token && !_loadPromise && !_hasData) _sharedLoadAll();

    return () => _subs.delete(sub);
  }, []);

  return { state: _state, update: _sharedUpdate, addAudit: _sharedAddAudit, reload: _sharedLoadAll };
};

/* ── Derived helpers (pure) ─────────────────────────────────── */
export const orgLicenses     = (state, orgId) => state.licenses.filter(l => l.org === orgId);
export const orgWorkspaces   = (state, orgId) => state.workspaces.filter(w => w.org === orgId);
export const orgMembers      = (state, orgId) => state.members.filter(m => m.org === orgId);
export const findWorkspace   = (state, wsId)  => state.workspaces.find(w => w.id === wsId);
export const licenseTotals   = (lics) => ({
  purchased: lics.reduce((s, l) => s + (l.purchased ?? 0), 0),
  used:      lics.reduce((s, l) => s + (l.used      ?? 0), 0),
});
export const defaultBranding = (org) => ({
  primaryColor: '#125AB0', accentColor: '#4692EC', logoText: org ? org.name : 'Organization',
  loginHeadline: org ? ('Welcome to ' + org.name) : 'Welcome', footer: '© 2026',
  domain: '', favicon: org ? org.name[0] : 'N',
});

/* workspaceMemberships / workspaceMembers / memberWorkspaces kept for view compatibility */
export const workspaceMemberships = (state, wsId) => state.memberships.filter(mb => mb.workspace === wsId);
export const workspaceMembers = (state, wsId) =>
  workspaceMemberships(state, wsId)
    .map(mb => ({ ...mb, member: state.members.find(m => m.id === mb.memberId) }))
    .filter(x => x.member);
export const memberWorkspaces = (state, memberId) =>
  state.memberships.filter(mb => mb.memberId === memberId)
    .map(mb => ({ ...mb, workspace: state.workspaces.find(w => w.id === mb.workspace) }))
    .filter(x => x.workspace);
